import React from "react";
import ServiceCtaButton from "./ServiceCtaButton";
import { CheckIcon, NAMED_ICONS } from "./serviceIcons";

const SectionHeading = ({ heading, introduction, titleDivider, spacing = "mb-8 sm:mb-10" }) => {
  if (!heading && !introduction) return null;
  return (
    <div className={`${spacing} max-w-3xl`}>
      {heading && (
        <h2
          className={
            titleDivider
              ? "flex items-center gap-3 text-base sm:text-lg font-bold text-gray-900 mb-3"
              : "text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3"
          }
        >
          {heading}
          {titleDivider && (
            <span className="flex-1 h-px bg-gray-200" aria-hidden="true" />
          )}
        </h2>
      )}
      {introduction && (
        <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
          {introduction}
        </p>
      )}
    </div>
  );
};

const ServiceBenefits = ({
  heading,
  introduction,
  items = [],
  variant = "cards",
  titleDivider,
}) => {
  if (!items.length) {
    return null;
  }

  if (variant === "checklist") {
    return (
      <div
        className="border-2 rounded-lg p-6 sm:p-8"
        style={{ borderColor: "#db0058" }}
      >
        {heading && (
          <h2 className="flex items-center gap-2 text-base sm:text-lg font-bold text-gray-900 mb-5">
            <span
              className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: "#db0058" }}
              aria-hidden="true"
            />
            {heading}
          </h2>
        )}
        {introduction && (
          <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-5">
            {introduction}
          </p>
        )}
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {items.map((item, index) => (
            <li
              key={item.title || index}
              className="flex items-start gap-2 text-sm sm:text-base text-gray-800"
            >
              <CheckIcon
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: "#db0058" }}
              />
              <span>
                {item.title}
                {item.description ? ` ${item.description}` : ""}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (variant === "pillars") {
    return (
      <div>
        <SectionHeading heading={heading} introduction={introduction} titleDivider={titleDivider} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          {items.map((item, index) => {
            const IconComp = item.icon && NAMED_ICONS[item.icon];
            return (
              <div
                key={item.title || index}
                className="rounded-lg p-6 sm:p-7 text-center"
                style={{ backgroundColor: "#232528" }}
              >
                {IconComp && (
                  <span
                    className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-4"
                    style={{ backgroundColor: "rgba(219,0,88,0.2)" }}
                  >
                    <IconComp className="w-5 h-5" style={{ color: "#db0058" }} />
                  </span>
                )}
                <h3 className="text-sm sm:text-base font-bold text-white mb-2">
                  {item.title}
                </h3>
                {item.description && (
                  <p
                    className="text-xs sm:text-sm leading-relaxed"
                    style={{ color: "rgba(255,255,255,0.65)" }}
                  >
                    {item.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (variant === "rows") {
    return (
      <div>
        <SectionHeading
          heading={heading}
          introduction={introduction}
          titleDivider={titleDivider}
          spacing="mb-6 sm:mb-8"
        />
        <div className="flex flex-col gap-2.5 sm:gap-3">
          {items.map((item, index) => (
            <div
              key={item.title || index}
              className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-5 flex items-start gap-3"
            >
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                style={{ backgroundColor: "#db0058" }}
              >
                <CheckIcon className="w-3.5 h-3.5 text-white" />
              </span>
              <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                {item.title && (
                  <strong className="text-gray-900">{item.title} </strong>
                )}
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "outcomes") {
    return (
      <div>
        <SectionHeading heading={heading} introduction={introduction} titleDivider={titleDivider} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {items.map((item, index) => (
            <div
              key={item.title || index}
              className="bg-gray-50 border border-gray-200 rounded-r-lg p-4 sm:p-5"
              style={{ borderLeft: "4px solid #db0058" }}
            >
              <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-1">
                {item.title}
              </h3>
              {item.description && (
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "chips") {
    return (
      <div>
        <SectionHeading heading={heading} introduction={introduction} titleDivider={titleDivider} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map((item, index) => {
            const IconComp = item.icon && NAMED_ICONS[item.icon];
            return (
              <div
                key={item.title || index}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-5 text-center flex flex-col items-center gap-2"
              >
                {IconComp && (
                  <IconComp
                    className="w-6 h-6"
                    style={{ color: "#db0058" }}
                  />
                )}
                <h3 className="text-xs sm:text-sm font-bold text-gray-900">
                  {item.title}
                </h3>
                {item.description && (
                  <p className="text-[11px] sm:text-xs text-gray-600 leading-snug">
                    {item.description}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {(heading || introduction) && (
        <div className="mb-10 sm:mb-12 max-w-3xl">
          {heading && (
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {heading}
            </h2>
          )}
          {introduction && (
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
              {introduction}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {items.map((item, index) => (
          <div
            key={item.title || index}
            className="bg-white p-6 sm:p-8 rounded-lg shadow-sm transition-shadow duration-300 hover:shadow-lg h-full flex flex-col"
          >
            {item.icon && (
              <img
                src={item.icon}
                alt=""
                aria-hidden="true"
                className="w-16 h-14 mb-6"
              />
            )}
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              {item.title}
            </h3>
            {item.description && (
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                {item.description}
              </p>
            )}
            {item.cta && (
              <div className="mt-4 pt-2">
                <ServiceCtaButton
                  cta={item.cta}
                  variant="outline"
                  size="small"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceBenefits;
